/**
 * push-notifications.js
 * PWA Push Notification system — SW-based delivery, notification center,
 * sound alerts, quiet hours, granular type preferences
 */
import { S, save, stampItem } from '../core/state.js';
import { esc, uid, now } from '../core/utils.js';
import { toast } from '../ui/layout.js';
import { lg } from '../modules/history.js';

// ─── CONSTANTS ───
const MAX_NOTIFICATIONS = 200; // Cap notification history
const _NOTIF_SOUND_URL = null;  // Optional: '/sounds/alert.mp3' (reserved for future custom sound)
let _audioCtx = null;

// ─── CORE: SW-BASED NOTIFICATION DELIVERY ───

/**
 * Send a notification via Service Worker (works when tab is backgrounded)
 * Falls back to Notification API, then to toast-only
 *
 * @param {string} type - 'tradeAlert'|'signalAlert'|'tdMaturity'|'priceTarget'|'portfolio'
 * @param {string} title
 * @param {string} body
 * @param {object} opts - { ticker, tag, action, url, requireInteraction, dedupeKey }
 * @returns {boolean} true if notification was sent
 */
export function swNotify(type, title, body, opts = {}) {
  const pn = S.settings.pushNotif || {};

  // 1. Check master notification toggle
  if (!(S.settings && S.settings.notif)) {
    // Still show toast as fallback
    toast(title + ': ' + body, 'a-y');
    return false;
  }

  // 2. Check type-specific toggle
  if (pn.types && pn.types[type] === false) {
    return false;
  }

  // 3. Check quiet hours
  if (pn.quietEnabled && _isQuietHour(pn.quietStart, pn.quietEnd)) {
    // During quiet hours, only record to history — no sound, no browser notification
    _recordNotification(type, title, body, opts);
    return false;
  }

  // 4. Dedupe check
  if (opts.dedupeKey && S.alertedKeys[opts.dedupeKey]) {
    return false;
  }
  if (opts.dedupeKey) {
    S.alertedKeys[opts.dedupeKey] = Date.now();
  }

  // 5. Record to notification history
  _recordNotification(type, title, body, opts);

  // 6. Play sound if enabled
  if (pn.sound) {
    _playAlertSound();
  }

  // 7. Send via SW (preferred) or fallback to Notification API
  if ('Notification' in window && Notification.permission === 'granted') {
    _sendViaSW(title, body, opts);
  }

  // 8. Always show in-app toast
  const toastColor = type === 'tradeAlert' ? 'a-y' : type === 'signalAlert' ? 'a-g' : 'a-b';
  toast(title + ': ' + body, toastColor);

  // 9. Log to history
  lg('alert', opts.ticker || '—', title + ' — ' + body);
  save();

  return true;
}

/**
 * Send notification via Service Worker postMessage
 */
function _sendViaSW(title, body, opts) {
  if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
    // Fallback: direct Notification API
    try {
      new Notification('SPEKTRX — ' + title, {
        body,
        icon: '/icons/icon-192.png',
        tag: opts.tag || 'spektrx-' + Date.now()
      });
    } catch (_e) { /* swallow */ }
    return;
  }

  navigator.serviceWorker.controller.postMessage({
    type: 'SHOW_NOTIFICATION',
    title: 'SPEKTRX — ' + title,
    body,
    tag: opts.tag || 'spektrx-' + Date.now(),
    action: opts.action || null,
    ticker: opts.ticker || null,
    url: opts.url || '/',
    requireInteraction: opts.requireInteraction || false,
    silent: false
  });
}

// ─── NOTIFICATION HISTORY ───

/**
 * Record notification to S.notifications
 */
function _recordNotification(type, title, body, opts) {
  if (!Array.isArray(S.notifications)) S.notifications = [];

  const n = {
    id: uid(),
    type,
    title,
    body,
    ticker: opts.ticker || null,
    action: opts.action || null,
    ts: now(),
    read: false,
    dismissed: false,
    _updatedAt: Date.now()
  };
  stampItem(n);

  S.notifications.unshift(n);

  // Cap
  if (S.notifications.length > MAX_NOTIFICATIONS) {
    S.notifications = S.notifications.slice(0, MAX_NOTIFICATIONS);
  }
}

/**
 * Get unread notification count
 */
export function getUnreadCount() {
  if (!Array.isArray(S.notifications)) return 0;
  return S.notifications.filter(n => !n.read && !n.dismissed).length;
}

/**
 * Mark a notification as read
 */
export function markNotifRead(notifId) {
  const n = (S.notifications || []).find(x => x.id === notifId);
  if (n) {
    n.read = true;
    stampItem(n);
    save();
    renderNotifCenter();
    _updateBellBadge();
  }
}

/**
 * Mark all notifications as read
 */
export function markAllNotifsRead() {
  (S.notifications || []).forEach(n => {
    if (!n.read) {
      n.read = true;
      stampItem(n);
    }
  });
  save();
  renderNotifCenter();
  _updateBellBadge();
}

/**
 * Dismiss a notification (hide from center)
 */
export function dismissNotif(notifId) {
  const n = (S.notifications || []).find(x => x.id === notifId);
  if (n) {
    n.dismissed = true;
    stampItem(n);
    save();
    renderNotifCenter();
    _updateBellBadge();
  }
}

/**
 * Clear all notifications
 */
export function clearAllNotifs() {
  S.notifications = [];
  save();
  renderNotifCenter();
  _updateBellBadge();
}

// ─── NOTIFICATION CENTER UI ───

let _centerOpen = false;
let _centerFilter = 'all'; // 'all' | 'unread' | type

/**
 * Toggle notification center panel
 */
export function toggleNotifCenter() {
  _centerOpen = !_centerOpen;
  const panel = document.getElementById('notifCenter');
  if (!panel) return;
  panel.style.display = _centerOpen ? 'block' : 'none';
  if (_centerOpen) renderNotifCenter();
}

/**
 * Close notification center
 */
export function closeNotifCenter() {
  _centerOpen = false;
  const panel = document.getElementById('notifCenter');
  if (panel) panel.style.display = 'none';
}

/**
 * Set notification center filter
 */
export function setNotifFilter(f) {
  _centerFilter = f;
  renderNotifCenter();
}

/**
 * Render notification center panel contents
 */
export function renderNotifCenter() {
  const body = document.getElementById('notifCenterBody');
  if (!body) return;

  const notifs = (S.notifications || []).filter(n => !n.dismissed);
  const filtered = _centerFilter === 'all' ? notifs
    : _centerFilter === 'unread' ? notifs.filter(n => !n.read)
    : notifs.filter(n => n.type === _centerFilter);

  // Stats
  const unread = notifs.filter(n => !n.read).length;
  const statsEl = document.getElementById('notifCenterStats');
  if (statsEl) {
    statsEl.innerHTML = `<span style="color:var(--t3)">${notifs.length} total</span>` +
      (unread > 0 ? ` · <span style="color:var(--acc)">${unread} unread</span>` : '');
  }

  // Filter bar
  const filterEl = document.getElementById('notifCenterFilter');
  if (filterEl) {
    const fBtn = (label, val) =>
      `<button class="btn-xs${_centerFilter === val ? ' active' : ''}" data-action="setNotifFilter" data-args='["${val}"]' style="${_centerFilter === val ? 'background:var(--acc);color:#fff' : ''}">${label}</button>`;
    filterEl.innerHTML = fBtn('All', 'all') + fBtn('Unread', 'unread') +
      fBtn('Trade', 'tradeAlert') + fBtn('Signal', 'signalAlert') + fBtn('TD', 'tdMaturity');
  }

  if (!filtered.length) {
    body.innerHTML = '<div class="neo-empty ctr">No notifications</div>';
    return;
  }

  body.innerHTML = filtered.slice(0, 50).map(n => {
    const icon = _typeIcon(n.type);
    const timeAgo = _timeAgo(n.ts);
    const readStyle = n.read ? 'opacity:0.6' : '';
    const dotColor = n.read ? 'transparent' : 'var(--acc)';

    return `<div class="notif-item" style="display:flex;gap:8px;padding:8px 12px;border-bottom:1px solid var(--bd);cursor:pointer;${readStyle}" data-action="markNotifRead" data-args='["${n.id}"]'>
      <div style="flex-shrink:0;margin-top:2px">
        <span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${dotColor};margin-right:4px"></span>
        <span class="notif-ico" style="font-size:14px">${icon}</span>${_typeGlyph(n.type)}
      </div>
      <div style="flex:1;min-width:0">
        <div style="font-size:12px;font-weight:600;color:var(--t1);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(n.title)}</div>
        <div style="font-size:11px;color:var(--t2);margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(n.body)}</div>
        <div style="font-size:10px;color:var(--t3);margin-top:2px">${timeAgo}${n.ticker ? ' · ' + esc(n.ticker) : ''}</div>
      </div>
      <button class="btn-xs" style="flex-shrink:0;font-size:10px;padding:2px 6px;align-self:center" data-action="dismissNotif" data-args='["${n.id}"]'>×</button>
    </div>`;
  }).join('');
}

/**
 * Update the notification bell badge count
 */
export function _updateBellBadge() {
  const badge = document.getElementById('notifBellBadge');
  if (!badge) return;
  const count = getUnreadCount();
  badge.textContent = count > 99 ? '99+' : count;
  badge.style.display = count > 0 ? 'flex' : 'none';
}

// ─── NOTIFICATION PREFERENCES UI ───

/**
 * Initialize push notification settings in the settings page
 */
export function initPushSettings() {
  const pn = S.settings.pushNotif || {};
  const el = (id) => document.getElementById(id);

  const soundEl = el('sPushSound');
  const quietEl = el('sPushQuiet');
  const quietStartEl = el('sPushQuietStart');
  const quietEndEl = el('sPushQuietEnd');

  if (soundEl) soundEl.checked = !!pn.sound;
  if (quietEl) quietEl.checked = !!pn.quietEnabled;
  if (quietStartEl) quietStartEl.value = pn.quietStart || '23:00';
  if (quietEndEl) quietEndEl.value = pn.quietEnd || '07:00';

  // Type toggles
  const types = pn.types || {};
  ['tradeAlert', 'signalAlert', 'tdMaturity', 'priceTarget', 'portfolio'].forEach(t => {
    const cb = el('sPush_' + t);
    if (cb) cb.checked = types[t] !== false;
  });
}

/**
 * Save push notification settings
 */
export function savePushSettings() {
  if (!S.settings.pushNotif) S.settings.pushNotif = {};
  const pn = S.settings.pushNotif;
  const el = (id) => document.getElementById(id);

  const soundEl = el('sPushSound');
  const quietEl = el('sPushQuiet');
  const quietStartEl = el('sPushQuietStart');
  const quietEndEl = el('sPushQuietEnd');

  if (soundEl) pn.sound = soundEl.checked;
  if (quietEl) pn.quietEnabled = quietEl.checked;
  if (quietStartEl) pn.quietStart = quietStartEl.value;
  if (quietEndEl) pn.quietEnd = quietEndEl.value;

  // Type toggles
  if (!pn.types) pn.types = {};
  ['tradeAlert', 'signalAlert', 'tdMaturity', 'priceTarget', 'portfolio'].forEach(t => {
    const cb = el('sPush_' + t);
    if (cb) pn.types[t] = cb.checked;
  });

  save();
  toast('Push notification settings saved', 'a-b');
}

/**
 * Send a test notification to verify everything works
 */
export function testPushNotif() {
  const btn = document.getElementById('pushTestBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }

  const success = swNotify('portfolio', 'Test Notification',
    'Push notifications are working. You should see this in both the notification center and as a browser notification.',
    { tag: 'test-' + Date.now(), ticker: 'TEST' }
  );

  setTimeout(() => {
    if (btn) { btn.disabled = false; btn.textContent = 'Test Notification'; }
    if (!success) {
      toast('Notification suppressed (check quiet hours or type settings)', 'a-y');
    }
  }, 500);
}

// ─── SW MESSAGE LISTENER (notification click navigation) ───

/**
 * Setup click-outside-to-close for notification center
 */
export function setupNotifClickOutside() {
  document.addEventListener('click', (e) => {
    if (!_centerOpen) return;
    const panel = document.getElementById('notifCenter');
    const bell = document.querySelector('[data-action="toggleNotifCenter"]');
    if (!panel) return;
    // Close if click is outside both panel and bell button
    if (!panel.contains(e.target) && (!bell || !bell.contains(e.target))) {
      closeNotifCenter();
    }
  });
}

/**
 * Setup listener for messages from Service Worker (e.g., notification clicks)
 */
export function setupSWMessageListener() {
  if (!navigator.serviceWorker) return;

  navigator.serviceWorker.addEventListener('message', event => {
    if (!event.data) return;

    if (event.data.type === 'NOTIFICATION_CLICK') {
      const { action, ticker } = event.data;
      // Navigate to relevant page based on action (use grouped nav)
      const target = action === 'trades' || (ticker && !action) ? 'trades'
        : action === 'signals' ? 'signals'
        : action === 'dashboard' ? 'dashboard' : null;
      if (target && window._navActivateTab) {
        window._navActivateTab(target);
      }
    }
  });
}

// ─── QUIET HOURS ───

function _isQuietHour(start, end) {
  if (!start || !end) return false;
  const nowDt = new Date();
  const h = nowDt.getHours();
  const m = nowDt.getMinutes();
  const current = h * 60 + m;

  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;

  // Handle overnight (e.g., 23:00 → 07:00)
  if (startMin > endMin) {
    return current >= startMin || current < endMin;
  }
  return current >= startMin && current < endMin;
}

// ─── SOUND ALERTS ───

function _playAlertSound() {
  try {
    // Use Web Audio API for a short beep (no external file needed)
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = _audioCtx.createOscillator();
    const gain = _audioCtx.createGain();
    osc.connect(gain);
    gain.connect(_audioCtx.destination);
    osc.frequency.value = 880; // A5 note
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, _audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, _audioCtx.currentTime + 0.3);
    osc.start(_audioCtx.currentTime);
    osc.stop(_audioCtx.currentTime + 0.3);
  } catch (_e) {
    // AudioContext may not be available
  }
}

// ─── HELPERS ───

// Same type map as _typeIcon, drawn as a thin line glyph. Hidden by default (main.css); the zen
// skin shows it in place of the emoji. Both are in the DOM so a skin switch needs no re-render.
const _GLYPH = {
  tradeAlert: '<path d="M8 3l5.5 9.5h-11z"/>',
  signalAlert: '<path d="M9 2L4 9h4l-1 5 5-7H8z"/>',
  tdMaturity: '<circle cx="8" cy="8.5" r="5.5"/><path d="M8 5.5v3l2 1.5"/>',
  priceTarget: '<circle cx="8" cy="8" r="6"/><circle cx="8" cy="8" r="2.2"/>',
  portfolio: '<path d="M3 13V8M8 13V3M13 13V6"/>',
  default: '<path d="M4 11V7.5a4 4 0 018 0V11l1 1.5H3z"/><path d="M6.5 13.5a1.5 1.5 0 003 0"/>',
};
function _typeGlyph(type) {
  const d = Object.prototype.hasOwnProperty.call(_GLYPH, type) ? _GLYPH[type] : _GLYPH.default;
  return `<svg class="notif-glyph" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
}

function _typeIcon(type) {
  switch (type) {
    case 'tradeAlert': return '&#x25B2;'; // ▲
    case 'signalAlert': return '&#x26A1;'; // ⚡
    case 'tdMaturity': return '&#x23F0;'; // ⏰
    case 'priceTarget': return '&#x1F3AF;'; // 🎯
    case 'portfolio': return '&#x1F4CA;'; // 📊
    default: return '&#x1F514;'; // 🔔
  }
}

function _timeAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
